import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Typography,
  Box,
  IconButton,
  Collapse,
  Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const ACTION_COLORS = {
  'invite.sent': 'info',
  'invite.accepted': 'success',
  'invite.revoked': 'warning',
  'invite.resent': 'info',
  'user.role_changed': 'primary',
  'user.deactivated': 'error',
  'settings.competition_defaults_updated': 'secondary',
  'settings.user_defaults_updated': 'secondary',
  'settings.feature_flags_updated': 'secondary',
  'competition_access.granted': 'success',
  'competition_access.revoked': 'error',
  'competition_access.role_changed': 'primary'
};

const formatAction = (action) => {
  return action
    .split('.')
    .map(part => part.replace(/_/g, ' '))
    .join(': ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const AuditLogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = log.old_value || log.new_value || (log.metadata && Object.keys(log.metadata).length > 0);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {hasDetails && (
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {formatDate(log.created_at)}
          </Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={log.user_id || 'System'}>
            <Typography variant="body2">
              {log.user_email || 'System'}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            label={formatAction(log.action)}
            size="small"
            color={ACTION_COLORS[log.action] || 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {log.resource_type}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {log.resource_id ? log.resource_id.substring(0, 8) + '...' : '-'}
          </Typography>
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Details
                </Typography>
                {log.old_value && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Previous Value:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'error.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                        {JSON.stringify(log.old_value, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {log.new_value && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      New Value:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'success.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                        {JSON.stringify(log.new_value, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Metadata:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditLogTable = ({
  logs,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange
}) => {
  if (logs.length === 0 && !loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No audit log entries found. Actions will be logged here as they occur.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <AuditLogRow key={log.id} log={log} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
};

export default AuditLogTable;
